import { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  ArrowForwardRounded,
  DeleteOutlineRounded,
  EditRounded,
  LinkRounded,
  MoreVert,
  Spellcheck,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'bytes';

import dayjs from 'dayjs';
import PDF from '../../../../assets/IconPdf.svg';
import IconFolder from '../../../../assets/IconFolder.svg';
import FolderPrivate from '../../../../assets/FolderPrivate.svg';
import LockPng from '../../../../assets/lock.png';

import { Arquive, Folder } from '../../../../services';

import { Buttons, Container, Div } from './styles';

import useAuthStore from '../../../../store/auth';
import useArquiveStore from '../../../../store/arquives';
import useCompanyStore from '../../../../store/companies';
import { getTypeStatus } from '../../../../utils/meetingHelpers';
import { Status } from '../../../Reuniao/components/MeetingCard/styles';

interface FolderCardProps {
  folder: Partial<Folder | Arquive | any>;
  folderIndex: number;
  ownerName: string;
  handleOpenEditDrawer: any;
  handleOpenMoveModal: any;
  handleOpenModalRemove: any;
  handleSignatureDrawer?: any;
  handleViewSignatureDrawer?: any;
  isFolder?: boolean;
  handleOpenFileViewer: any;
  isAutomatic?: boolean;
}

export const FolderCard = ({
  folder,
  ownerName,
  folderIndex,
  handleOpenEditDrawer,
  handleOpenMoveModal,
  handleOpenModalRemove,
  handleSignatureDrawer,
  handleViewSignatureDrawer,
  isFolder = false,
  isAutomatic = false,
  handleOpenFileViewer,
}: FolderCardProps) => {
  const navigate = useNavigate();

  const user = useAuthStore(state => state.user);
  const selectCompany = useCompanyStore(state => state.selectedCompany);
  const setSearchForFilesAndFoldersAndLinks = useArquiveStore(
    state => state.setSearchArchives,
  );

  const breadcrumbs = useArquiveStore(state => state.breadcrumbs);
  const setBreadcrumbs = useArquiveStore(state => state.setBreadcrumbs);
  const userOnCompany = selectCompany?.users?.some(u => u.id === user?.id);
  const [openDropdown, setOpenDropdown] = useState<null | HTMLElement>(null);
  const [hasAllSigned, setHasAllSigned] = useState<boolean>(false);
  const [validateAmountChildren, setValidateAmountChildren] = useState(0);
  const open = Boolean(openDropdown);
  const isCreator = ownerName === user?.name;

  useEffect(() => {
    if (folder?.signatures?.length > 0) {
      const allSigned = folder?.signatures[0]?.subscribers?.every(
        (sub: any) => sub.isSigned === true,
      );

      if (allSigned) {
        setHasAllSigned(true);
      }
    }
  }, []);

  const childrenAmount = folder.childrens?.filter(
    (c: any) => c?.canceled_at === null,
  );

  useEffect(() => {
    const children = childrenAmount
      ?.map((c: any) => {
        if (user?.profile === 'UN1' && !userOnCompany) {
          return c;
        }
        if (
          !c?.guests?.length ||
          c.guests.some((u: any) => u?.email === user?.email)
        ) {
          return c;
        }
        return null;
      })
      ?.filter(Boolean);

    setValidateAmountChildren(children?.length);
  }, [
    childrenAmount,
    user?.email,
    user?.id,
    user?.name,
    user?.profile,
    userOnCompany,
    validateAmountChildren,
  ]);

  const getSizeOrItensLength = useCallback((isFold: boolean, item: any) => {
    if (isFold) {
      const itensLength = item.childrens?.length;

      if (itensLength > 1 || itensLength === 0) {
        return 'Itens';
      }

      return 'Item';
    }

    return 'Tamanho';
  }, []);

  const getIcon = useCallback((item: any) => {
    switch (item.type) {
      case 'folder':
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={item?.guests?.length > 0 ? FolderPrivate : IconFolder}
              alt="Icone PDF"
              style={{ width: '38px', height: '38px' }}
            />
          </Box>
        );
      case 'file':
        if (item?.is_encrypted) {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                src={LockPng}
                alt="Icone PDF"
                style={{ width: '35px', height: '35px' }}
              />
            </Box>
          );
        }
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={PDF}
              alt="Icone PDF"
              style={{ width: '35px', height: '35px' }}
            />
          </Box>
        );

      default:
        return (
          <LinkRounded
            sx={{ width: '35px', height: '35px', color: '#2196F3' }}
          />
        );
    }
  }, []);

  const handleFileNavigate = useCallback(() => {
    const newBreadcrumb = {
      name: folder.name,
      route: `/folder/${folder.id}`,
      id: folder.id,
    };

    setBreadcrumbs([...breadcrumbs, { ...newBreadcrumb }]);

    navigate(`folder/${folder.id}`);
  }, [breadcrumbs, folder, navigate, setBreadcrumbs]);

  const handlePreviewFile = useCallback(async () => {
    await handleOpenFileViewer(folder.id);
  }, [handleOpenFileViewer, folder]);

  const handleArquiveNavigation = useCallback(
    (item: any) => {
      // onClick={() =>
      //   isFolder
      //     ? handleFileNavigate()
      //     : !userOnCompany
      //     ? null
      //     : handlePreviewFile()
      // }

      if (item.type === 'folder') {
        setSearchForFilesAndFoldersAndLinks('');
        return handleFileNavigate();
      }
      if (userOnCompany) {
        if (item.type === 'file') {
          return handlePreviewFile();
        }

        let { url } = item;

        if (url.includes('http') || url.includes('https')) {
          return window.open(url, '_blank');
        }

        url = `https://${url}`;

        return window.open(url, '_blank');
      }
      return null;
    },
    [
      handleFileNavigate,
      handlePreviewFile,
      setSearchForFilesAndFoldersAndLinks,
      userOnCompany,
    ],
  );

  const handleOpenMenu = (event: any) => {
    event.stopPropagation();
    setOpenDropdown(event.currentTarget);
  };

  const handleCloseMenu = (event: any) => {
    event.stopPropagation();
    setOpenDropdown(null);
  };

  const handleDropDownWithSignature = (event: any) => {
    handleSignatureDrawer(folder, isFolder, event);
    setOpenDropdown(null);
  };

  const handleDropDownWithEdit = (event: any) => {
    handleOpenEditDrawer(folder, folderIndex, folder.type, event);
    setOpenDropdown(null);
  };

  const handleDropDownWithMove = (event: any) => {
    handleOpenMoveModal(folder, isFolder, event);
    setOpenDropdown(null);
  };

  const handleDropDownViewSignature = (event: any) => {
    handleViewSignatureDrawer(folder, isFolder, event);
    setOpenDropdown(null);
  };

  const handleDropDownRemove = (event: any) => {
    handleOpenModalRemove(folder, isFolder, event);
    setOpenDropdown(null);
  };

  return (
    <Container
      key={folder?.id}
      className="folder-card-hover"
      style={{
        cursor: isFolder
          ? 'pointer'
          : userOnCompany
          ? 'pointer'
          : 'not-allowed',
        background: folder.canceled_at ? 'rgb(255 217 218 / 54%)' : '#ffffff',
      }}
      onClick={() => handleArquiveNavigation(folder)}
    >
      <Div
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          width: '100%',
          height: '100%',
        }}
      >
        {getIcon(folder)}
        <Tooltip title={isFolder ? folder.name : folder.original_name}>
          <div
            className={
              folder?.meetings?.length > 0 ? 'name' : 'nameWithoutMeeting'
            }
          >
            {isFolder ? folder.name : folder.original_name}
          </div>
        </Tooltip>
      </Div>
      <Div
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          gap: '45px',
          height: '100%',
        }}
      >
        {!isFolder &&
          folder?.signatures?.length > 0 &&
          (user?.profile === 'UN1' || user?.profile === 'UN2') && (
            <Tooltip
              title={
                hasAllSigned ? 'Assinatura completa' : 'Pendente de assinatura'
              }
            >
              <Spellcheck
                sx={{ color: hasAllSigned ? '#4CAF50' : '#FF9800' }}
              />
            </Tooltip>
          )}
        {isFolder && folder?.meetings?.length > 0 && (
          <Box
            sx={{ display: 'flex', width: '150px', justifyContent: 'center' }}
          >
            <Status
              sx={{
                background: getTypeStatus(folder?.meetings[0]?.status)?.color,
              }}
            >
              {getTypeStatus(folder?.meetings[0]?.status)?.status}
            </Status>
          </Box>
        )}
        {isFolder && folder?.meetings?.length > 0 && (
          <p className="creator">
            {dayjs(folder?.meetings[0]?.start_at).format('DD/MM/YYYY')}
            <span>Data da reunião</span>
          </p>
        )}
        <p className="items">
          <span className="sizeFiles">
            {isFolder ? validateAmountChildren || 0 : format(folder?.size)}
          </span>
          <span className="subtitle">
            {getSizeOrItensLength(isFolder, folder)}
          </span>
        </p>
        <Tooltip title={ownerName}>
          <p className="creator">
            {ownerName} <span>Criado por</span>
          </p>
        </Tooltip>
        <p className="data">
          {folder?.lastModification} <span>Modificado</span>
        </p>
        {folder?.is_automatic ||
        ((user?.profile === 'UN3' || user?.profile === 'UN4') && !isCreator) ||
        folder?.canceled_at ? null : (
          <Buttons disabled={false}>
            <IconButton onClick={event => handleOpenMenu(event)}>
              <MoreVert sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
            </IconButton>
          </Buttons>
        )}
        <Menu
          id="basic-menu"
          anchorEl={openDropdown}
          open={open}
          onClose={event => handleCloseMenu(event)}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
            style: { width: '240px' },
          }}
        >
          <MenuItem
            onClick={event => handleDropDownWithEdit(event)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: 'rgba(0, 0, 0, 0.54)',
            }}
          >
            <EditRounded sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
            <Box sx={{ fontSize: '15px', fontWeight: '500', color: '#000000' }}>
              Editar
            </Box>
          </MenuItem>
          {!isAutomatic && (
            <MenuItem
              onClick={event => handleDropDownWithMove(event)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'rgba(0, 0, 0, 0.54)',
              }}
            >
              <ArrowForwardRounded sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
              <Box
                sx={{ fontSize: '15px', fontWeight: '500', color: '#000000' }}
              >
                Mover
              </Box>
            </MenuItem>
          )}
          {!isFolder && folder?.signatures?.length > 0 && (
            <MenuItem
              onClick={event => handleDropDownViewSignature(event)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'rgba(0, 0, 0, 0.54)',
              }}
            >
              <Spellcheck sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
              <Box
                sx={{ fontSize: '15px', fontWeight: '500', color: '#000000' }}
              >
                Assinaturas
              </Box>
            </MenuItem>
          )}
          {!isFolder && folder?.signatures?.length === 0 && (
            <MenuItem
              onClick={event => handleDropDownWithSignature(event)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'rgba(0, 0, 0, 0.54)',
              }}
            >
              <Spellcheck sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
              <Box
                sx={{
                  fontSize: '15px',
                  fontWeight: '500',
                  color: '#000000',
                }}
              >
                Solicitar Assinatura
              </Box>
            </MenuItem>
          )}
          <MenuItem
            onClick={event => handleDropDownRemove(event)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: 'rgba(0, 0, 0, 0.54)',
            }}
          >
            <DeleteOutlineRounded sx={{ color: '#D01317' }} />
            <Box sx={{ fontSize: '15px', fontWeight: '500', color: '#D01317' }}>
              Deletar
            </Box>
          </MenuItem>
        </Menu>
      </Div>
    </Container>
  );
};
